<template>
  <el-dialog
      title="Thiết lập lý do xin nghỉ"
      :visible.sync="dialogVisible"
      width="780px"
      :before-close="closeDialog"
      :close-on-click-modal="false"
      top="5vh"
  >
    <div v-loading="loading" class="reason-config-wrapper">
      <!-- Header actions -->
      <div class="reason-header">
        <span class="reason-count">Tổng: {{ reasons.length }} lý do</span>
        <div class="reason-header-right">
          <el-button
              type="primary"
              size="small"
              icon="el-icon-plus"
              :disabled="customReasonCount >= 20"
              @click="openCreateForm"
          >
            Thêm lý do mới
          </el-button>
        </div>
      </div>

      <div v-if="customReasonCount >= 20" class="limit-warning">
        <i class="el-icon-warning"></i> Đã đạt giới hạn tối đa 20 lý do tùy chỉnh
      </div>

      <!-- Form thêm mới (chỉ hiện khi tạo custom reason mới) -->
      <transition name="el-fade-in">
        <div v-if="formVisible" class="reason-form-card">
          <p class="form-title">Thêm lý do mới</p>
          <el-form :model="form" :rules="formRules" ref="reasonForm" label-position="top" size="small">
            <el-form-item label="Tên lý do" prop="displayName">
              <el-input
                  v-model="form.displayName"
                  placeholder="Nhập tên lý do (1-100 ký tự)"
                  :maxlength="100"
                  show-word-limit
              />
            </el-form-item>
            <el-form-item label="Nội dung gợi ý" prop="suggestedContent">
              <el-input
                  v-model="form.suggestedContent"
                  type="textarea"
                  :rows="2"
                  placeholder="Nội dung tự động điền khi phụ huynh chọn lý do này (tối đa 500 ký tự)"
                  :maxlength="500"
                  show-word-limit
              />
            </el-form-item>
            <div class="form-actions">
              <el-button size="small" @click="cancelForm">Huỷ</el-button>
              <el-button type="primary" size="small" :loading="formLoading" @click="submitCreateForm">
                Thêm mới
              </el-button>
            </div>
          </el-form>
        </div>
      </transition>

      <!-- Danh sách lý do -->
      <transition-group name="reason-move" tag="div" class="reason-list">
        <div
            v-for="(item, index) in reasons"
            :key="item.id"
            class="reason-item"
            :class="{
              'is-disabled': !item.enabled,
              'is-highlighted': highlightedId === item.id
            }"
        >
          <!-- Cột thứ tự & di chuyển -->
          <div class="reason-order">
            <span class="order-badge">{{ index + 1 }}</span>
            <div class="order-buttons">
              <el-tooltip content="Di chuyển lên" placement="top" :open-delay="400">
                <button
                    v-if="index > 0"
                    type="button"
                    class="order-arrow-btn"
                    @click="moveUp(index)"
                >
                  <i class="el-icon-caret-top"></i>
                </button>
              </el-tooltip>
              <el-tooltip content="Di chuyển xuống" placement="bottom" :open-delay="400">
                <button
                    v-if="index < reasons.length - 1"
                    type="button"
                    class="order-arrow-btn"
                    @click="moveDown(index)"
                >
                  <i class="el-icon-caret-bottom"></i>
                </button>
              </el-tooltip>
            </div>
          </div>

          <!-- Toggle bật/tắt -->
          <div class="reason-toggle">
            <el-switch
                v-model="item.enabled"
                active-color="#67C23A"
            />
          </div>

          <!-- Thông tin lý do (inline editable) -->
          <div class="reason-info">
            <div class="reason-name-row">
              <!-- System: text tĩnh -->
              <span v-if="item.isSystemReason" class="reason-name-text">{{ item.displayName }}</span>
              <!-- Custom: input inline -->
              <el-input
                  v-else
                  v-model="item.displayName"
                  size="mini"
                  class="inline-name-input"
                  placeholder="Tên lý do"
                  :maxlength="100"
              />
              <el-tag
                  :type="item.isSystemReason ? '' : 'warning'"
                  size="mini"
                  effect="plain"
                  class="reason-type-badge"
              >
                {{ item.isSystemReason ? 'Hệ thống' : 'Tùy chỉnh' }}
              </el-tag>
            </div>
            <!-- Nội dung gợi ý: textarea inline (cả system & custom) -->
            <el-input
                v-model="item.suggestedContent"
                type="textarea"
                :autosize="{ minRows: 1, maxRows: 3 }"
                size="mini"
                class="inline-suggestion-input"
                placeholder="Nhập nội dung gợi ý..."
                :maxlength="500"
            />
          </div>

          <!-- Actions -->
          <div class="reason-actions">
            <el-button
                v-if="!item.isSystemReason"
                type="text"
                size="mini"
                class="btn-delete"
                @click="handleDelete(item)"
            >
              <i class="el-icon-delete"></i>
            </el-button>
          </div>
        </div>
      </transition-group>

      <div v-if="reasons.length === 0 && !loading" class="empty-state">
        <i class="el-icon-folder-opened"></i>
        <p>Không có dữ liệu</p>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button type="danger" size="medium" @click="closeDialog">
        <i class="el-icon-circle-close"/> Đóng
      </el-button>
      <el-button type="success" size="medium" :loading="saving" @click="saveAll">
        <i class="el-icon-circle-check"/> Lưu tất cả
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import AbsentLetterReasonConfigService from "@/absent_letter_v3/services/AbsentLetterReasonConfigService";

export default {
  name: "AbsentLetterReasonConfig",
  props: {
    dialogVisible: {type: Boolean, default: false},
  },
  data() {
    return {
      loading: false,
      saving: false,
      formLoading: false,
      reasons: [],
      originalReasons: [], // snapshot ban đầu để so sánh thay đổi
      formVisible: false,
      form: {
        displayName: "",
        suggestedContent: "",
      },
      formRules: {
        displayName: [
          {required: true, message: "Vui lòng nhập tên lý do", trigger: "blur"},
          {min: 1, max: 100, message: "Tên lý do từ 1-100 ký tự", trigger: "blur"},
        ],
        suggestedContent: [
          {max: 500, message: "Nội dung gợi ý tối đa 500 ký tự", trigger: "blur"},
        ],
      },
      // Reorder state
      highlightedId: null,
      highlightTimer: null,
    };
  },
  computed: {
    customReasonCount() {
      return this.reasons.filter(r => !r.isSystemReason).length;
    },
  },
  beforeDestroy() {
    clearTimeout(this.highlightTimer);
  },
  methods: {
    // ─── LOAD DATA ────────────────────────────────────────────────
    async fetchList() {
      this.loading = true;
      try {
        const res = await AbsentLetterReasonConfigService.getList();
        const list = (res.data.data || []).map(item => ({...item}));
        this.reasons = list;
        // Lưu snapshot để so sánh khi save
        this.originalReasons = JSON.parse(JSON.stringify(list));
      } catch (e) {
        this.$message.error("Lỗi khi tải danh sách lý do xin nghỉ");
      } finally {
        this.loading = false;
      }
    },

    // ─── FORM: THÊM MỚI ──────────────────────────────────────────
    openCreateForm() {
      this.form = {displayName: "", suggestedContent: ""};
      this.formVisible = true;
      this.$nextTick(() => {
        if (this.$refs.reasonForm) this.$refs.reasonForm.clearValidate();
      });
    },

    cancelForm() {
      this.formVisible = false;
      this.form = {displayName: "", suggestedContent: ""};
    },

    submitCreateForm() {
      this.$refs.reasonForm.validate(async valid => {
        if (!valid) return;
        this.formLoading = true;
        try {
          await AbsentLetterReasonConfigService.createCustomReason({
            displayName: this.form.displayName,
            suggestedContent: this.form.suggestedContent,
          });
          this.$message.success("Thêm lý do mới thành công!");
          this.formVisible = false;
          this.form = {displayName: "", suggestedContent: ""};
          await this.fetchList();
        } catch (e) {
          const msg = e?.response?.data?.message || "Có lỗi xảy ra";
          this.$message.error(msg);
        } finally {
          this.formLoading = false;
        }
      });
    },

    // ─── LƯU TẤT CẢ ──────────────────────────────────────────────
    async saveAll() {
      // Validate: tên custom reason không được để trống
      const emptyName = this.reasons.find(r => !r.isSystemReason && !(r.displayName || "").trim());
      if (emptyName) {
        this.$message.error("Tên lý do không được để trống");
        return;
      }

      // Kiểm tra có thay đổi gì không
      const hasChanges = JSON.stringify(this.reasons) !== JSON.stringify(this.originalReasons);
      if (!hasChanges) {
        this.$message.info("Không có thay đổi nào cần lưu");
        return;
      }

      this.saving = true;
      try {
        const payload = this.reasons.map(item => ({
          id: item.id,
          reasonType: item.reasonType,
          enabled: item.enabled,
          displayName: item.isSystemReason ? undefined : (item.displayName || "").trim(),
          suggestedContent: (item.suggestedContent || "").trim() || null,
        }));

        await AbsentLetterReasonConfigService.batchUpdate(payload);
        this.$message.success("Lưu thành công!");
        await this.fetchList(); // refresh snapshot
      } catch (e) {
        const msg = e?.response?.data?.message || "Có lỗi xảy ra khi lưu";
        this.$message.error(msg);
      } finally {
        this.saving = false;
      }
    },

    // ─── XOÁ CUSTOM REASON ───────────────────────────────────────
    handleDelete(item) {
      this.$confirm(
          `Xác nhận xoá lý do "${item.displayName}"? <br>Thao tác này không thể hoàn tác.`,
          "Thông báo!",
          {
            distinguishCancelAndClose: true,
            closeOnClickModal: false,
            dangerouslyUseHTMLString: true,
            confirmButtonText: "Xác nhận",
            cancelButtonText: "Huỷ bỏ",
          }
      ).then(async () => {
        try {
          await AbsentLetterReasonConfigService.deleteCustomReason(item.id);
          this.$message.success("Xoá lý do thành công!");
          await this.fetchList();
        } catch (e) {
          const msg = e?.response?.data?.message || "Xoá thất bại";
          this.$message.error(msg);
        }
      });
    },

    // ─── DI CHUYỂN THỨ TỰ ────────────────────────────────────────
    moveUp(index) {
      if (index === 0) return;
      const movedItem = this.reasons[index];
      const list = [...this.reasons];
      [list[index - 1], list[index]] = [list[index], list[index - 1]];
      this.reasons = list;
      this.flashHighlight(movedItem.id);
    },

    moveDown(index) {
      if (index === this.reasons.length - 1) return;
      const movedItem = this.reasons[index];
      const list = [...this.reasons];
      [list[index], list[index + 1]] = [list[index + 1], list[index]];
      this.reasons = list;
      this.flashHighlight(movedItem.id);
    },

    flashHighlight(id) {
      this.highlightedId = id;
      clearTimeout(this.highlightTimer);
      this.highlightTimer = setTimeout(() => {
        this.highlightedId = null;
      }, 600);
    },

    // ─── ĐÓNG DIALOG ──────────────────────────────────────────────
    closeDialog() {
      this.formVisible = false;
      this.form = {displayName: "", suggestedContent: ""};
      this.$emit("dialog-close");
    },
  },
};
</script>

<style scoped lang="scss">
.reason-config-wrapper {
  min-height: 200px;
}

.reason-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  .reason-count {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
  }

  .reason-header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.limit-warning {
  background: #fef3c7;
  color: #92400e;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 12px;
}

// ─── FORM CARD ──────────────────────────────────────────────────────
.reason-form-card {
  background: #f0f7ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;

  .form-title {
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
}

// ─── TRANSITION GROUP ANIMATION ─────────────────────────────────────
.reason-move-move {
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.reason-move-enter-active,
.reason-move-leave-active {
  transition: all 0.3s ease;
}

.reason-move-enter,
.reason-move-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

// ─── DANH SÁCH LÝ DO ───────────────────────────────────────────────
.reason-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 52vh;
  overflow-y: auto;
  padding: 4px;
}

.reason-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  transition: all 0.25s ease;

  &:hover {
    border-color: #93c5fd;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.06);
  }

  &.is-disabled {
    opacity: 0.55;
    background: #f8fafc;
  }

  &.is-highlighted {
    border-color: #3b82f6;
    background: #eff6ff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
}

// ─── CỘT THỨ TỰ ───────────────────────────────────────────────────
.reason-order {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 58px;
  padding-top: 4px;

  .order-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .order-buttons {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 24px;
  }

  .order-arrow-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 20px;
    border: none;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
    color: #64748b;
    font-size: 16px;
    transition: all 0.15s ease;
    padding: 0;

    &:hover {
      background: #e0f2fe;
      color: #2563eb;
    }

    &:active {
      background: #bfdbfe;
      transform: scale(0.9);
    }
  }
}

// ─── TOGGLE ─────────────────────────────────────────────────────────
.reason-toggle {
  flex-shrink: 0;
  padding-top: 4px;
}

// ─── THÔNG TIN (INLINE EDITABLE) ───────────────────────────────────
.reason-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;

  .reason-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .reason-name-text {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  .reason-type-badge {
    font-size: 11px;
    flex-shrink: 0;
  }

  .inline-name-input {
    flex: 1;
    max-width: 280px;

    /deep/ .el-input__inner {
      font-size: 14px;
      font-weight: 600;
      color: #1e293b;
      border-color: transparent;
      background: transparent;
      padding: 0 8px;
      height: 28px;
      line-height: 28px;
      border-radius: 6px;
      transition: all 0.2s;

      &:hover {
        border-color: #e2e8f0;
        background: #f8fafc;
      }

      &:focus {
        border-color: #93c5fd;
        background: #ffffff;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
      }
    }
  }

  .inline-suggestion-input {
    /deep/ .el-textarea__inner {
      font-size: 12px;
      color: #475569;
      border-color: transparent;
      background: transparent;
      padding: 4px 8px;
      border-radius: 6px;
      resize: none;
      transition: all 0.2s;
      line-height: 1.5;

      &::placeholder {
        color: #94a3b8;
        font-style: italic;
      }

      &:hover {
        border-color: #e2e8f0;
        background: #f8fafc;
      }

      &:focus {
        border-color: #93c5fd;
        background: #ffffff;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
      }
    }
  }
}

// ─── ACTIONS ────────────────────────────────────────────────────────
.reason-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding-top: 4px;

  .btn-delete {
    color: #ef4444 !important;
    font-size: 16px;

    &:hover {
      color: #dc2626 !important;
    }
  }
}

// ─── EMPTY STATE ────────────────────────────────────────────────────
.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #94a3b8;

  i {
    font-size: 40px;
    margin-bottom: 8px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
}

// ─── ELEMENT DIALOG OVERRIDE ────────────────────────────────────────
::v-deep .el-dialog {
  border-radius: 16px;
  overflow: hidden;
}

::v-deep .el-dialog__header {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  padding: 18px 24px !important;
}

::v-deep .el-dialog__title {
  color: white !important;
  font-size: 19px !important;
  font-weight: 600;
}

::v-deep .el-dialog__headerbtn .el-dialog__close {
  color: white !important;
  font-size: 22px;
}

::v-deep .el-dialog__body {
  max-height: 74vh;
  overflow-y: auto;
  background: #f8fafc;
  padding: 20px 24px !important;
}

::v-deep .el-dialog__footer {
  background: #f1f5f9;
  padding: 14px 24px !important;
  border-top: 1px solid #e2e8f0;
}
</style>
